import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowPathIcon,
  StopIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ClockIcon,
  PlayIcon,
} from '@heroicons/react/24/outline';
import { useCollaborations } from '@/hooks/useCollaborations';
import type { Collaboration, CollaborationStatus } from '@/types';

interface AsyncPollingStatusProps {
  requestId: string;
  endpointId?: string;
  initialPollingInterval?: number;
  onComplete?: (collaboration: Collaboration) => void;
  onError?: (error: string) => void;
}

export default function AsyncPollingStatus({
  requestId,
  initialPollingInterval = 3000,
  onComplete,
  onError,
}: AsyncPollingStatusProps) {
  const [isPolling, setIsPolling] = useState(true);
  const [pollCount, setPollCount] = useState(0);
  const [pollingInterval, setPollingInterval] = useState(initialPollingInterval);

  // Query collaborations by requestId
  const {
    data: collaborationsData,
    isLoading,
    error,
    refetch,
  } = useCollaborations(1, 1, { requestId });

  const collaboration = collaborationsData?.data?.[0];
  const latestStatus = collaboration?.collaborationStatuses?.slice(-1)[0];

  // Check if collaboration is complete
  const isComplete = latestStatus?.status === 'completed' || latestStatus?.status === 'failed';

  // Handle completion
  useEffect(() => {
    if (isComplete && collaboration) {
      setIsPolling(false);
      if (latestStatus?.status === 'completed') {
        onComplete?.(collaboration);
      } else if (latestStatus?.status === 'failed') {
        onError?.(latestStatus.error || 'Collaboration failed');
      }
    }
  }, [isComplete, collaboration, latestStatus, onComplete, onError]);

  // Polling effect
  useEffect(() => {
    if (!isPolling || isComplete) return;

    const interval = setInterval(() => {
      refetch();
      setPollCount(c => c + 1);

      // Increase interval after many polls (exponential backoff)
      if (pollCount > 10 && pollingInterval < 10000) {
        setPollingInterval(prev => Math.min(prev * 1.5, 10000));
      }
    }, pollingInterval);

    return () => clearInterval(interval);
  }, [isPolling, isComplete, pollingInterval, pollCount, refetch]);

  // Manual refresh
  const handleRefresh = useCallback(() => {
    refetch();
    setPollCount(c => c + 1);
  }, [refetch]);

  // Toggle polling
  const togglePolling = useCallback(() => {
    setIsPolling(prev => !prev);
  }, []);

  // Get status display info
  const getStatusInfo = (status?: CollaborationStatus) => {
    if (!status) {
      return {
        label: 'Pending',
        color: 'text-yellow-500',
        bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
        borderColor: 'border-yellow-200 dark:border-yellow-800',
        icon: ClockIcon,
      };
    }

    switch (status.status) {
      case 'completed':
        return {
          label: 'Completed',
          color: 'text-green-500',
          bgColor: 'bg-green-50 dark:bg-green-900/20',
          borderColor: 'border-green-200 dark:border-green-800',
          icon: CheckCircleIcon,
        };
      case 'failed':
        return {
          label: 'Failed',
          color: 'text-red-500',
          bgColor: 'bg-red-50 dark:bg-red-900/20',
          borderColor: 'border-red-200 dark:border-red-800',
          icon: ExclamationCircleIcon,
        };
      case 'running':
        return {
          label: 'Running',
          color: 'text-blue-500',
          bgColor: 'bg-blue-50 dark:bg-blue-900/20',
          borderColor: 'border-blue-200 dark:border-blue-800',
          icon: ArrowPathIcon,
        };
      case 'skipped':
        return {
          label: 'Skipped',
          color: 'text-gray-500',
          bgColor: 'bg-gray-50 dark:bg-gray-900/20',
          borderColor: 'border-gray-200 dark:border-gray-800',
          icon: ExclamationCircleIcon,
        };
      case 'waiting':
        return {
          label: 'Waiting',
          color: 'text-purple-500',
          bgColor: 'bg-purple-50 dark:bg-purple-900/20',
          borderColor: 'border-purple-200 dark:border-purple-800',
          icon: ArrowPathIcon,
        };
      default:
        return {
          label: 'Pending',
          color: 'text-yellow-500',
          bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
          borderColor: 'border-yellow-200 dark:border-yellow-800',
          icon: ClockIcon,
        };
    }
  };

  const statusInfo = getStatusInfo(latestStatus);
  const StatusIcon = statusInfo.icon;

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <div className="flex items-center space-x-2">
          <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
          <span className="text-sm text-red-700 dark:text-red-300">
            Error checking status: {error instanceof Error ? error.message : 'Unknown error'}
          </span>
        </div>
        <button
          onClick={handleRefresh}
          className="mt-3 inline-flex items-center px-3 py-1.5 text-sm font-medium text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/40 rounded hover:bg-red-200 dark:hover:bg-red-900/60"
        >
          <ArrowPathIcon className="h-4 w-4 mr-1" />
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className={`${statusInfo.bgColor} border ${statusInfo.borderColor} rounded-lg p-4`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <StatusIcon
            className={`h-6 w-6 ${statusInfo.color} ${
              !isComplete && isPolling ? 'animate-spin' : ''
            }`}
          />
          <div>
            <h3 className="text-sm font-medium text-gray-900 dark:text-white">
              {statusInfo.label}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Request: {requestId.slice(0, 8)}...
            </p>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center space-x-2">
          <button
            onClick={handleRefresh}
            disabled={isLoading}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-white/50 dark:hover:bg-black/20 rounded transition-colors"
            title="Refresh"
          >
            <ArrowPathIcon className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>

          {!isComplete && (
            <button
              onClick={togglePolling}
              className={`p-2 rounded transition-colors ${
                isPolling
                  ? 'text-red-500 hover:text-red-700 hover:bg-red-100 dark:hover:bg-red-900/30'
                  : 'text-green-500 hover:text-green-700 hover:bg-green-100 dark:hover:bg-green-900/30'
              }`}
              title={isPolling ? 'Stop auto-polling' : 'Start auto-polling'}
            >
              {isPolling ? (
                <StopIcon className="h-4 w-4" />
              ) : (
                <PlayIcon className="h-4 w-4" />
              )}
            </button>
          )}
        </div>
      </div>

      {/* Status Details */}
      {latestStatus && (
        <div className="space-y-3">
          {latestStatus.description && (
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {latestStatus.description}
            </p>
          )}

          {latestStatus.error && (
            <div className="p-3 bg-red-100 dark:bg-red-900/40 rounded border border-red-200 dark:border-red-700">
              <p className="text-sm text-red-700 dark:text-red-300">{latestStatus.error}</p>
            </div>
          )}

          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
            <span>
              Last updated: {new Date(latestStatus.updatedAt || latestStatus.createdAt).toLocaleTimeString()}
            </span>
            {!isComplete && isPolling && (
              <span className="flex items-center">
                <ArrowPathIcon className="h-3 w-3 mr-1 animate-spin" />
                Polling every {Math.round(pollingInterval / 1000)}s
              </span>
            )}
          </div>
        </div>
      )}

      {/* Output Preview */}
      {isComplete && collaboration?.output && (
        <div className="mt-4">
          <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-2">
            Output
          </h4>
          <pre className="p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 text-xs font-mono text-gray-800 dark:text-gray-200 overflow-auto max-h-40">
            {JSON.stringify(collaboration.output, null, 2)}
          </pre>
        </div>
      )}

      {/* Link to full collaboration */}
      {collaboration && (
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Link
            to={`/collaborations/${collaboration.id}`}
            className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline"
          >
            View full collaboration details
          </Link>
        </div>
      )}
    </div>
  );
}
